import { useState } from 'react';

import { RunsTable } from '@/components/RunsTable';
import { useSearchModel } from '@/components/RunsTable/hooks/useSearchModel';
import { TextOverflowTooltip } from '@/components/TextOverflowTooltip';
import { Badge, Text } from '@/design-system';
import {
  GridColumnVisibilityModel,
  GridTimeModel,
  useDataGridState,
} from '@/hooks/useDataGridState';
import { RunSchema } from '@/types/schema';

export const CategoryTraces = ({
  runs,
  columnVisibility,
  clusterJobId,
  jobTimeRange,
  loadMoreRuns,
  hasMoreRuns,
  isLoading,
  isValidating,
  error,
  attributes,
}: {
  runs: RunSchema[];
  columnVisibility: GridColumnVisibilityModel;
  clusterJobId: string;
  jobTimeRange: GridTimeModel;
  loadMoreRuns: () => void;
  hasMoreRuns: boolean;
  isLoading: boolean;
  isValidating: boolean;
  error: unknown;
  attributes: string[];
}) => {
  const { searchModel, setSearchModel } = useSearchModel();
  const [timeModel, setTimeModel] = useState<GridTimeModel>(jobTimeRange ?? {});

  const {
    sortModel,
    paginationModel,
    filterModel,
    setSortModel,
    onColumnVisibilityResetClick,
    setPaginationModel,
    resetPaginationModel,
    setFilterModel,
    initialState,
  } = useDataGridState({
    defaultPaginationModel: {
      pageIndex: 0,
      pageSize: 50,
    },
  });

  return (
    <RunsTable
      key={clusterJobId}
      runsFilter={null}
      debouncedRunsFilter={null}
      virtual={false}
      view="all"
      tableDisplay="list"
      withToolbar={false}
      withTypeCol={true}
      withPagination={true}
      searchModel={searchModel}
      setSearchModel={setSearchModel}
      sortModel={sortModel}
      columnVisibilityModel={columnVisibility}
      paginationModel={paginationModel}
      filterModel={filterModel}
      setSortModel={setSortModel}
      setColumnVisibilityModel={() => {}}
      onColumnVisibilityResetClick={onColumnVisibilityResetClick}
      setPaginationModel={setPaginationModel}
      resetPaginationModel={resetPaginationModel}
      setFilterModel={setFilterModel}
      timeModel={timeModel}
      setTimeModel={setTimeModel}
      initialState={initialState}
      externalRunsState={{
        runs,
        isLoading,
        isValidating,
        error,
        hasMoreRuns,
        onLoadMore: loadMoreRuns,
      }}
      extraColumns={[
        {
          id: 'summary',
          header: 'Summary',
          size: 500,
          display: (row) => {
            return (
              <TextOverflowTooltip
                className="line-clamp-1 truncate"
                textVariant="sm"
              >
                {row.summary}
              </TextOverflowTooltip>
            );
          },
        },
        ...attributes.map((attribute) => ({
          id: `attribute-${attribute}`,
          header: attribute,
          size: 100,
          display: (row) => {
            return <Attribute value={row.attributes?.[attribute]} />;
          },
        })),
      ]}
    />
  );
};

const Attribute = ({ value }: { value: string | number | boolean }) => {
  switch (typeof value) {
    case 'string':
    case 'number':
      return (
        <Text variant="sm" weight="medium">
          {value}
        </Text>
      );
    case 'boolean':
      return (
        <Badge color={value ? 'success' : 'error'} size="xs">
          {value.toString()}
        </Badge>
      );
  }
};
